import type { RenderedView } from '../router.js';
import { navigate } from '../router.js';
import { repos } from '../store.js';
import { esc, onClick, toast } from '../dom.js';
import { icon } from '../icons.js';
import {
  analyzeQuiz,
  difficultyBand,
  discriminationBand,
  distractorFindings,
  hasDistractorFlag,
  NON_FUNCTIONING_DISTRACTOR_RATE,
  type ItemStat,
  type QuizAnalysis,
} from '../../analysis/itemAnalysis.js';
import { buildRawCsv, buildItemAnalysisCsv, buildMpsCsv, shareOrDownload, safeName, type CsvRow } from '../../export/csv.js';
import type { QuizRecord, AnswerKeyRecord, StudentRecord, ResultRecord, SettingsRecord } from '../../db/schema.js';

const OPTS = ['A', 'B', 'C', 'D'];

interface Ctx {
  title: string;
  subtitle: string;
  back: string;
  quiz: QuizRecord;
  key: AnswerKeyRecord | null;
  rows: CsvRow[];
  students: StudentRecord[];
  results: ResultRecord[];
  fileTag: string;
  section: string;
  /** route to the per-competency gap report for the same scope. */
  gapsHref: string;
  /** route to the printable / Excel version of the same scope. */
  reportsHref: string;
  /** competencies mapped for this quiz (0 = no TOS yet). */
  competencyCount: number;
  settings: SettingsRecord;
}

const num2 = (v: number | null) => (v == null ? '—' : v.toFixed(2));
const decisionClass = (d: string | null) => (d === 'Retain' ? 'badge-ok' : d === 'Reject' ? 'badge-danger' : d === 'Revise' ? 'badge-warn' : 'badge-muted');

/**
 * Four bars, one per DepEd band, sized by percentage. Plain divs and the brand
 * palette — a charting library for four bars would cost more offline precache
 * budget than the whole analysis layer.
 *
 * Placed next to the SD deliberately: "mean 28, SD 9" tells a teacher nothing
 * they can picture, but a two-humped set of bars does.
 */
function bandHistogram(analysis: QuizAnalysis): string {
  const rows = analysis.bandDistribution
    .map(
      (b) => `<div class="band-row">
        <div class="band-head">
          <span class="band-range">${esc(b.range)}</span>
          <span class="band-desc">${esc(b.descriptor || '—')}</span>
          <span class="band-n">${b.count} · ${b.pct}%</span>
        </div>
        <div class="band-track"><span class="band-bar band-${b.tier}" style="width:${Math.max(b.pct, b.count ? 1 : 0)}%"></span></div>
      </div>`,
    )
    .join('');
  return `<p class="section-label">Score distribution by DepEd band</p>
    <div class="bands">${rows}</div>
    <p class="hint">DepEd SY 2026–2027 reference tiers. The reference table specifies these for competency-level percentages; here they are applied to each learner's overall quiz percentage, rounded to a whole number before banding. The 80–100 band has no descriptor in the table.</p>`;
}

/** One option's cell: the count, with the percentage under it (— when suppressed). */
function optionCell(it: ItemStat, o: string): string {
  const count = it.distractors[o] ?? 0;
  const pct = it.optionPct ? `${it.optionPct[o]}%` : '—';
  const keyed = o === it.key;
  return `<td class="num dcell${keyed ? ' dcell-key' : ''}">${keyed ? `<strong>${count}</strong>` : count}<span class="dcell-sub">${pct}</span></td>`;
}

/**
 * Per-item distractor breakdown. Defaults to the items that already carry a flag
 * plus any with a distractor finding: on a 100-item quiz the unfiltered table is
 * unreadable, and the existing flag says THAT something is wrong while this table
 * says WHAT. Non-matching rows stay in the DOM and are hidden by a class, so the
 * "Show all items" toggle costs no re-render.
 */
function distractorTable(analysis: QuizAnalysis): string {
  const interesting = (it: ItemStat) => !!it.flag || hasDistractorFlag(it);
  const shown = analysis.items.filter(interesting).length;
  const rows = analysis.items
    .map((it) => {
      const findings = distractorFindings(it);
      const badges = findings
        ? findings.map((f) => `<span class="badge badge-warn">${esc(f)}</span>`).join(' ')
        : '<span class="muted">—</span>';
      const flagBadge = it.flag ? `<span class="badge ${it.flag === 'too_easy' ? 'badge-warn' : 'badge-danger'}">${esc(it.flag.replace('_', ' '))}</span>` : '';
      return `<tr class="${interesting(it) ? 'flagged' : ''}">
        <td>${it.q}</td><td class="num">${esc(it.key)}</td>
        ${OPTS.map((o) => optionCell(it, o)).join('')}
        <td class="num">${it.blank}</td><td class="num">${it.double}</td>
        <td>${flagBadge}${flagBadge && findings?.length ? ' ' : ''}${badges}</td></tr>`;
    })
    .join('');

  return `<p class="section-label">${icon.chart()} Distractor analysis</p>
    <div class="table-wrap"><table class="data dtab only-flagged"><thead><tr>
      <th>Item</th><th class="num">Key</th>${OPTS.map((o) => `<th class="num">${o}</th>`).join('')}
      <th class="num">Blank</th><th class="num">Double</th><th>Findings</th></tr></thead>
    <tbody>${rows}</tbody></table></div>
    <button class="btn btn-ghost btn-sm btn-block" data-all-items>Show all items (${analysis.items.length})</button>
    <p class="hint">Showing the ${shown} flagged item${shown === 1 ? '' : 's'} of ${analysis.items.length}. The keyed answer is bold and outlined; every other option is a distractor. Blanks and double-marks are counted on their own and never attributed to an option.
    A distractor chosen by fewer than ${Math.round(NON_FUNCTIONING_DISTRACTOR_RATE * 100)}% of examinees is <em>non-functioning</em> — a measurement-literature convention (Haladyna &amp; Downing), not a DepEd rule. One chosen by more upper- than lower-27% learners <em>attracts the top group</em>, which is what a negative D actually means.
    Percentages and findings need ≥10 learners with varied scores; the counts are shown at any n. The upper/lower split per option is in the Excel export.</p>`;
}

function renderAnalysis(c: Ctx): RenderedView {
  const analysis = c.key ? analyzeQuiz(c.results, c.key, OPTS) : null;

  const mpsCard = analysis
    ? `<div class="card"><div class="stack">
        <div><span class="big-mps">${analysis.mps}%</span> <span class="badge badge-muted">MPS · ${esc(analysis.masteryLevel)}</span></div>
        <div class="muted" style="font-size:14px">${analysis.n} students · mean ${analysis.meanScore}/${analysis.maxScore} · SD ${analysis.stdDev} (${analysis.sdPctOfMax}% of max) · high ${analysis.highest} · low ${analysis.lowest}</div>
        <div class="muted" style="font-size:14px">KR-20 reliability: <strong>${num2(analysis.kr20)}</strong></div>
        ${!analysis.reliableSample ? `<span class="badge badge-warn">${icon.alert()} Small sample (n=${analysis.n}) — item stats are less reliable below 30 students.</span>` : ''}
        ${!analysis.statsAvailable ? `<span class="badge badge-muted">Discrimination, point-biserial &amp; KR-20 need ≥10 students with varied scores (shown as —).</span>` : ''}
      </div></div>`
    : `<div class="card"><span class="badge badge-warn">${icon.alert()} Set an answer key to compute MPS & item analysis.</span></div>`;

  const distMax = analysis ? Math.max(1, ...analysis.distribution.map((d) => d.count)) : 1;
  const dist = analysis && analysis.distribution.length
    ? `<p class="section-label">Score distribution</p><div class="dist">${analysis.distribution
        .map((d) => `<div class="dist-row"><span class="dist-k">${d.score}</span><span class="dist-bar" style="width:${Math.round((100 * d.count) / distMax)}%"></span><span class="dist-n">${d.count}</span></div>`)
        .join('')}</div>`
    : '';
  // Descriptive, so it renders at any n — unlike the item statistics below.
  const bands = analysis && analysis.n ? bandHistogram(analysis) : '';
  const distractors = analysis ? distractorTable(analysis) : '';

  // §1.6 Item Analysis report: Upper/Lower group counts, difficulty & its Level +
  // Decision, discrimination & its Level + Decision.
  const table = analysis
    ? `<p class="section-label">${icon.chart()} Item analysis</p>
       <div class="table-wrap"><table class="data"><thead><tr>
         <th>Item</th><th class="num">Upper</th><th class="num">Lower</th>
         <th class="num">Diff. (p)</th><th>Level</th><th>Decision</th>
         <th class="num">Disc. (D)</th><th>Level</th><th>Decision</th></tr></thead>
       <tbody>${analysis.items
         .map((it) => {
           const db = difficultyBand(it.difficulty);
           const disc = discriminationBand(it.discrimination);
           return `<tr><td>${it.q}</td>
             <td class="num">${it.upperCorrect == null ? '—' : `${it.upperCorrect}/${it.nUpper}`}</td>
             <td class="num">${it.lowerCorrect == null ? '—' : `${it.lowerCorrect}/${it.nLower}`}</td>
             <td class="num">${it.difficulty.toFixed(2)}</td><td>${esc(db.level)}</td>
             <td><span class="badge ${decisionClass(db.decision)}">${db.decision}</span></td>
             <td class="num">${num2(it.discrimination)}</td><td>${esc(disc.level)}</td>
             <td>${disc.decision ? `<span class="badge ${decisionClass(disc.decision)}">${disc.decision}</span>` : '—'}</td></tr>`;
         })
         .join('')}</tbody></table></div>
       <p class="hint">Upper/Lower = correct in the top/bottom 27%. Retain difficulty 0.41–0.60; retain discrimination 0.41+. Negative D → recheck the key. — = sample too small.</p>`
    : '';

  // §2.6 Per-Item MPS report: percentage-correct per item + a Below-75% flag.
  const mpsPerItem = analysis
    ? `<p class="section-label">Per-item MPS</p>
       <div class="table-wrap"><table class="data"><thead><tr><th>Item</th><th class="num">Correct</th><th class="num">MPS</th><th>Below 75%?</th></tr></thead>
       <tbody>${analysis.items
         .map((it) => {
           const itemMps = Math.round(it.difficulty * 100);
           return `<tr><td>${it.q}</td><td class="num">${it.correct}/${it.total}</td><td class="num">${itemMps}%</td>
             <td>${itemMps < 75 ? `<span class="badge badge-danger">Yes</span>` : ''}</td></tr>`;
         })
         .join('')}</tbody></table></div>
       <p class="hint">Overall MPS <strong>${analysis.mps}%</strong> · ${esc(analysis.masteryLevel)}. Items below 75% are not yet mastered — target for remediation.</p>`
    : '';

  // Items are only a proxy for competencies. With a TOS mapped, the same responses
  // roll up to per-competency gaps and a named remediation list.
  const gapsLink = c.competencyCount
    ? `<button class="btn btn-ghost btn-block" data-gaps style="margin-top:6px">${icon.list()} Competency half of this report (${c.competencyCount} competencies)</button>
       <p class="hint">Rolls these items up to learning competencies: who needs remediation on each and at which DepEd intervention tier, plus each learner's mastered / least-mastered competencies.</p>`
    : `<button class="btn btn-ghost btn-block" data-tos style="margin-top:6px">${icon.plus()} Add a Table of Specifications</button>
       <p class="hint">Item numbers alone can't name a learning competency. Map each competency to its item numbers and the same scans also report per competency and per learner.</p>`;

  return {
    title: 'Analysis & export',
    subtitle: c.subtitle,
    back: c.back,
    html: `<div class="view">
      ${mpsCard}${bands}${dist}${table}${distractors}${mpsPerItem}
      ${gapsLink}
      <p class="section-label">Export</p>
      <div class="stack">
        <button class="btn btn-primary btn-block" data-reports>${icon.doc()} Print / Excel (A4, tables)</button>
        <button class="btn btn-ghost btn-block" data-dl="raw">${icon.download()} Raw data CSV (per student + ANSWER_KEY)</button>
        <button class="btn btn-ghost btn-block" data-dl="items" ${analysis ? '' : 'disabled'}>${icon.download()} Item analysis CSV</button>
        <button class="btn btn-ghost btn-block" data-dl="mps" ${analysis ? '' : 'disabled'}>${icon.download()} MPS report CSV</button>
      </div>
      <p class="hint">On a phone these open the share sheet (email/Drive); on desktop they download.</p>
    </div>`,
    mount(root) {
      onClick(root, '[data-all-items]', (btn) => {
        const t = root.querySelector('.dtab');
        if (!t || !analysis) return;
        const all = t.classList.toggle('only-flagged') === false;
        btn.textContent = all ? 'Show flagged items only' : `Show all items (${analysis.items.length})`;
      });
      onClick(root, '[data-gaps]', () => navigate(c.gapsHref));
      onClick(root, '[data-reports]', () => navigate(c.reportsHref));
      onClick(root, '[data-tos]', () => navigate(`#/quiz/${c.quiz.id}/competencies`));
      onClick(root, '[data-dl="raw"]', async () => {
        // School/teacher come from Settings — these columns exported blank before.
        const meta = { school: c.settings.schoolName, teacher: c.settings.teacherName };
        const how = await shareOrDownload(`${safeName(c.quiz.title)}_${c.fileTag}_raw.csv`, buildRawCsv(c.quiz, c.rows, c.students, c.key, meta));
        toast(how === 'shared' ? 'Shared raw CSV' : 'Downloaded raw CSV');
      });
      onClick(root, '[data-dl="items"]', async () => {
        if (!analysis) return;
        const how = await shareOrDownload(`${safeName(c.quiz.title)}_${c.fileTag}_item_analysis.csv`, buildItemAnalysisCsv(c.quiz, analysis));
        toast(how === 'shared' ? 'Shared item analysis' : 'Downloaded item analysis');
      });
      onClick(root, '[data-dl="mps"]', async () => {
        if (!analysis) return;
        const how = await shareOrDownload(`${safeName(c.quiz.title)}_${c.fileTag}_mps.csv`, buildMpsCsv(c.quiz, analysis, c.section));
        toast(how === 'shared' ? 'Shared MPS report' : 'Downloaded MPS report');
      });
    },
  };
}

export async function assignmentAnalysisView(params: Record<string, string>): Promise<RenderedView> {
  const assignment = await repos.getAssignment(params.id);
  if (!assignment) return { title: 'Analysis', back: '/quizzes', html: `<div class="view"><p class="empty">Not found.</p></div>` };
  const quiz = (await repos.getQuiz(assignment.quizId))!;
  const [key, results, students, cls, map, settings] = await Promise.all([
    repos.answerKeyFor(quiz.id),
    repos.resultsForAssignment(assignment.id),
    repos.studentsInClass(assignment.classId),
    repos.getClass(assignment.classId),
    repos.competencyMapFor(quiz.id),
    repos.getSettings(),
  ]);
  const rows: CsvRow[] = results.map((r) => ({ result: r, gradeLevel: cls?.gradeLevel ?? '', section: cls?.name ?? '', date: assignment.dateAdministered }));
  return renderAnalysis({
    title: 'Analysis & export',
    subtitle: `${quiz.title} · ${cls?.name ?? ''}`,
    back: `/assignment/${assignment.id}/review`,
    quiz,
    key: key ?? null,
    rows,
    students,
    results,
    fileTag: safeName(cls?.name ?? 'class'),
    section: cls?.name ?? '',
    gapsHref: `#/assignment/${assignment.id}/gaps`,
    reportsHref: `#/assignment/${assignment.id}/reports`,
    competencyCount: map?.competencies.length ?? 0,
    settings,
  });
}

export async function quizAnalysisView(params: Record<string, string>): Promise<RenderedView> {
  const quiz = await repos.getQuiz(params.id);
  if (!quiz) return { title: 'Analysis', back: '/quizzes', html: `<div class="view"><p class="empty">Quiz not found.</p></div>` };
  const [key, assignments, classes, map, settings] = await Promise.all([
    repos.answerKeyFor(quiz.id),
    repos.assignmentsForQuiz(quiz.id),
    repos.listClasses(),
    repos.competencyMapFor(quiz.id),
    repos.getSettings(),
  ]);
  const classById = new Map(classes.map((c) => [c.id, c]));
  const asgById = new Map(assignments.map((a) => [a.id, a]));
  const results = await repos.resultsForQuiz(quiz.id);
  const studentLists = await Promise.all(assignments.map((a) => repos.studentsInClass(a.classId)));
  const students = studentLists.flat();
  const rows: CsvRow[] = results.map((r) => {
    const a = asgById.get(r.quizAssignmentId);
    const cls = a ? classById.get(a.classId) : undefined;
    return { result: r, gradeLevel: cls?.gradeLevel ?? quiz.gradeLevel, section: cls?.name ?? '', date: a?.dateAdministered ?? '' };
  });
  return renderAnalysis({
    title: 'Analysis & export',
    subtitle: `${quiz.title} · all classes`,
    back: `/quiz/${quiz.id}`,
    quiz,
    key: key ?? null,
    rows,
    students,
    results,
    fileTag: 'all',
    section: 'ALL',
    gapsHref: `#/quiz/${quiz.id}/gaps`,
    reportsHref: `#/quiz/${quiz.id}/reports`,
    competencyCount: map?.competencies.length ?? 0,
    settings,
  });
}
